import './FPlusCodeEditor.css'

import type { Optional } from '@festina-life/core'
import {
  type FPlusClientDiagnostic,
  type FPlusSuggestController,
  ThemeType,
} from '@festina-life/fplus-monaco'
import { useThemeContext } from '@festina-life/ui/theme'
import type * as monaco from 'monaco-editor'
import type { IDisposable } from 'monaco-editor'
import { useEffect, useRef } from 'react'

import type {
  FPlusCodeEditorLanguageServerInput,
  ITextModel,
} from '../../monaco/types/monaco'
import { createEditor, relayoutEditor } from './helpers'
export type Props = {
  initialCode: Optional<string>
  onChangeErrors?: (nextErrors: FPlusClientDiagnostic[]) => void
  onChangeCode: (nextValue: string) => void
  editorInput: FPlusCodeEditorLanguageServerInput
  options?: monaco.editor.IStandaloneEditorConstructionOptions
  isSizedByContent?: boolean
  onBlur?: () => void
  onFocus?: () => void
  onError?: (error: Error) => void
}

const FPlusCodeEditor = ({
  initialCode,
  onChangeCode,
  onChangeErrors,
  editorInput,
  options,
  isSizedByContent = false,
  onBlur,
  onFocus,
  onError,
}: Props) => {
  const { isDarkMode } = useThemeContext()

  const editorRef = useRef<monaco.editor.IStandaloneCodeEditor>(undefined)
  const ref = useRef<HTMLDivElement>(null)
  const disposables = useRef<IDisposable[]>([])
  const initialCodeRef = useRef(initialCode)

  useEffect(() => {
    if (!ref.current || editorRef.current) {
      return
    }

    const createdEditor = createEditor({
      node: ref.current,
      theme: isDarkMode ? ThemeType.dark : ThemeType.light,
      options,
    })

    const suggestController = createdEditor.getContribution(
      'editor.contrib.suggestController',
    ) as FPlusSuggestController

    if (suggestController.widget) {
      const suggestWidget = suggestController.widget.value

      if (suggestWidget && suggestWidget._setDetailsVisible) {
        suggestWidget._setDetailsVisible(true)
      }
    }

    disposables.current.push(createdEditor)

    if (initialCodeRef.current) {
      createdEditor.getModel()?.setValue(initialCodeRef.current)
    }

    const onChangeHandler = createdEditor.getModel()?.onDidChangeContent(() => {
      onChangeCode(editorRef.current?.getModel()!.getValue() ?? '')

      if (ref.current && isSizedByContent && editorRef.current) {
        relayoutEditor(editorRef.current, ref.current)
      }
    })

    if (onChangeHandler) {
      disposables.current.push(onChangeHandler)
    }

    if (onBlur) {
      const disposeBlur = createdEditor?.onDidBlurEditorText(onBlur)
      disposables.current.push(disposeBlur)
    }

    if (onFocus) {
      const disposeFocus = createdEditor?.onDidFocusEditorText(onFocus)
      disposables.current.push(disposeFocus)
    }

    const editorModel = createdEditor?.getModel() as ITextModel

    addControlKeyEvents(createdEditor, editorModel)

    if (editorInput && editorModel) {
      editorModel.hoverOnCtrl = editorInput.hoverOnCtrl
      editorModel.textTransformer = editorInput.textTransformer
      editorModel.languageServerInput = editorInput.languageServerInput
      editorModel.setCompiles = (validationStatus) =>
        onChangeErrors?.(validationStatus.diagnostics)
    }

    createdEditor?.focus()
    editorRef.current = createdEditor

    const disposableValues = disposables.current

    return () => {
      if (editorRef.current) {
        const model = editorRef.current.getModel()

        if (model) {
          model.dispose()
        }
        editorRef.current.dispose()
        editorRef.current = undefined

        while (disposableValues.length) {
          const disposable = disposableValues.pop()
          disposable?.dispose()
        }
      }
    }
  }, [
    isDarkMode,
    isSizedByContent,
    editorInput,
    onBlur,
    onChangeCode,
    onChangeErrors,
    onFocus,
    options,
    onError,
  ])

  return (
    <div
      data-meta="festinaEditor"
      ref={ref}
      className="size-full min-h-[inherit]"
      onClick={(evt) => evt.stopPropagation()}
    />
  )
}

function addControlKeyEvents(
  editor: monaco.editor.IStandaloneCodeEditor,
  model: ITextModel,
) {
  editor.onKeyDown((e) => {
    if (e.ctrlKey) {
      model.ctrlIsPressed = true
    }
  })
  editor.onKeyUp((e) => {
    if (e.ctrlKey) {
      model.ctrlIsPressed = false
    }
  })
}

export { FPlusCodeEditor }
